import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import type { ProjectFolder } from "../../api/projectFolders";
import type { ApiError } from "../../types/api";
import type { Project } from "../../types/project";
import type { StoryMapSummary } from "../../types/storyMap";
import ArchiveRow from "./ArchiveRow";
import FolderBreadcrumbs, { type FolderActions } from "./FolderBreadcrumbs";
import FolderRow from "./FolderRow";
import type { ArchivePlace, FolderRename } from "./ProjectTablePane";
import StoryRow from "./StoryRow";
import UpRow from "./UpRow";
import {
  type FolderIndex,
  type FolderKey,
  hiddenByFolder,
  normalizeFolderId,
  pathLabel,
  subtreeCounts,
} from "./folderTree";
import { FROZEN_RIGHT, frozenHeadSx } from "./frozenColumns";
import { type StorySortKey, type StorySortSpec, nextStorySort } from "./sortRows";
import { type StoryIndex, storyScopeLabel, subtreeStoryCount } from "./storyRows";
import { screenToLayout } from "../../utils/uiZoom";

interface Column {
  key: StorySortKey | null;
  label: string;
  align?: "right";
  searchOnly?: boolean;
  frozenRight?: number;
  edgePad?: boolean;
}

// Same frozen tail as the project table (pin, eye, show on map, menu) so the
// shared FolderRow lines up; a story row leaves the first two empty.
const COLUMNS: Column[] = [
  { key: "title", label: "Story map" },
  { key: null, label: "Folder", searchOnly: true },
  { key: "scope", label: "Draws from" },
  { key: "status", label: "Status" },
  { key: "scenes", label: "Scenes", align: "right" },
  { key: "updated", label: "Updated" },
  { key: "author", label: "Author" },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.pin },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.eye },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.locate },
  { key: null, label: "", frozenRight: FROZEN_RIGHT.menu, edgePad: true },
];

const SCROLL_CONTEXT_ROWS = 2;
const NO_DRAG = {};

export interface StoryTablePaneProps {
  mode: "explorer" | "search";
  index: FolderIndex;
  openFolderId: FolderKey;
  /** Sub-folders of the open folder (explorer mode). */
  folders: ProjectFolder[];
  /** The story rows, already filtered and sorted. */
  stories: StoryMapSummary[];
  storyIndex: StoryIndex;
  /** Every active project, for folder counts. */
  allProjects: Project[];
  isLoading: boolean;
  error: ApiError | null;
  highlightedStoryIds: ReadonlySet<string>;
  highlightedFolderId: number | null;
  /** Set when the map's hover settled: scroll this row into view. */
  scrollToStoryId: string | null;
  sort: StorySortSpec | null;
  onSortChange: (sort: StorySortSpec | null) => void;
  viewOnly: boolean;
  searchSummary?: { count: number; onClear: () => void };
  folderActions: FolderActions | null;
  onOpenFolder: (id: FolderKey) => void;
  onHoverStory: (id: string | null) => void;
  onHoverFolder: (id: number | null) => void;
  onOpenStory: (story: StoryMapSummary) => void;
  onPreviewStory: (story: StoryMapSummary) => void;
  onCopyShareLink: (story: StoryMapSummary) => void;
  onDeleteStory: (story: StoryMapSummary) => void;
  storyHasLocation: (story: StoryMapSummary) => boolean;
  onLocateStory: (story: StoryMapSummary) => void;
  onRequestRenameFolder: (folder: ProjectFolder) => void;
  onRequestMoveFolder: (folder: ProjectFolder) => void;
  onRequestDeleteFolder: (folder: ProjectFolder) => void;
  onRequestFolderBoundary: (folder: ProjectFolder) => void;
  onToggleFolderMapVisible: (folder: ProjectFolder) => void;
  onToggleFolderPin: (folder: ProjectFolder) => void;
  onLocateFolder: (folder: ProjectFolder) => void;
  folderHasLocation: (folder: ProjectFolder) => boolean;
  renamingFolder?: FolderRename | null;
  viewFilter?: {
    active: boolean;
    hiddenStories: number;
    hiddenFolders: number;
    onShowAll: () => void;
  };
  /** Rendered at the right-hand end of the breadcrumbs strip (the Projects | Story maps switch). */
  headerEnd?: React.ReactNode;
  /** The Archive: its row at the top level, or the pane's contents (archived projects' stories) when open. */
  archive?: ArchivePlace;
}

/**
 * The right-hand pane while browsing story maps: the same breadcrumb trail
 * and sub-folder rows as the project table, with story maps as the leaf rows.
 * No drag and drop — a story never changes project.
 */
export default function StoryTablePane({
  mode,
  index,
  openFolderId,
  folders,
  stories,
  storyIndex,
  allProjects,
  isLoading,
  error,
  highlightedStoryIds,
  highlightedFolderId,
  scrollToStoryId,
  sort,
  onSortChange,
  viewOnly,
  searchSummary,
  folderActions,
  onOpenFolder,
  onHoverStory,
  onHoverFolder,
  onOpenStory,
  onPreviewStory,
  onCopyShareLink,
  onDeleteStory,
  storyHasLocation,
  onLocateStory,
  onRequestRenameFolder,
  onRequestMoveFolder,
  onRequestDeleteFolder,
  onRequestFolderBoundary,
  onToggleFolderMapVisible,
  onToggleFolderPin,
  onLocateFolder,
  folderHasLocation,
  renamingFolder,
  viewFilter,
  headerEnd,
  archive,
}: StoryTablePaneProps) {
  const archiveOpen = !!archive?.open;
  const columns = COLUMNS.filter((c) => !c.searchOnly || mode === "search" || archiveOpen);
  const colCount = columns.length;

  // -- scroll-into-view from a map hover (same shape as the project table) --
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const suppressUntilRef = useRef(0);
  useEffect(() => {
    if (!scrollToStoryId) return;
    const el = rowRefs.current[scrollToStoryId];
    const container = containerRef.current;
    if (!el || !container) return;
    const header = container.querySelector("thead")?.getBoundingClientRect().height ?? 0;
    const margin = el.offsetHeight * SCROLL_CONTEXT_ROWS;
    const box = container.getBoundingClientRect();
    const row = el.getBoundingClientRect();
    const topLimit = box.top + header + margin;
    const bottomLimit = box.bottom - margin;
    let delta = 0;
    if (row.top < topLimit) delta = row.top - topLimit;
    else if (row.bottom > bottomLimit) delta = row.bottom - bottomLimit;
    if (delta === 0) return;
    suppressUntilRef.current = performance.now() + 400;
    // delta is in screen px (client rects); scrollBy takes layout px.
    container.scrollBy({ top: screenToLayout(delta), behavior: "smooth" });
  }, [scrollToStoryId]);

  const hoverStory = useCallback(
    (id: string) => (hovering: boolean) => {
      if (!hovering) {
        onHoverStory(null);
        return;
      }
      if (performance.now() < suppressUntilRef.current) return;
      onHoverStory(id);
    },
    [onHoverStory]
  );
  const hoverFolder = useCallback(
    (id: number) => (hovering: boolean) => onHoverFolder(hovering ? id : null),
    [onHoverFolder]
  );

  const parentOfOpen: FolderKey = openFolderId === null ? null : (index.byId.get(openFolderId)?.parent ?? null);
  const parentName = parentOfOpen === null ? "All projects" : index.byId.get(parentOfOpen)?.name ?? "the folder above";
  const showUpRow = mode === "explorer" && (openFolderId !== null || archiveOpen);
  const showArchiveRow = !!archive && mode === "explorer" && openFolderId === null && !archiveOpen;
  const storyCount = useCallback(
    (folderId: number) => subtreeStoryCount(storyIndex, index, folderId),
    [storyIndex, index]
  );
  const projectsById = useMemo(() => {
    const m = new Map<number, Project>();
    allProjects.forEach((p) => m.set(Number(p.id), p));
    return m;
  }, [allProjects]);
  // Where a story is filed: its project's folder, its own folder, or the top.
  const folderKeyOf = (story: StoryMapSummary): FolderKey => {
    if (story.project_id !== null && story.project_id !== undefined) {
      const project = projectsById.get(Number(story.project_id));
      return project ? normalizeFolderId(index, project.folder) : null;
    }
    return normalizeFolderId(index, story.folder_id);
  };

  const explorerEmpty = mode === "explorer" && folders.length === 0 && stories.length === 0 && !showArchiveRow;
  const searchEmpty = mode === "search" && stories.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 48,
        }}
      >
        {mode === "search" && searchSummary ? (
          <>
            <Typography sx={{ fontWeight: 700 }}>
              Search results · {searchSummary.count} story map{searchSummary.count === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={searchSummary.onClear} sx={{ textTransform: "none" }}>
              Clear search
            </Button>
          </>
        ) : (
          <FolderBreadcrumbs
            index={index}
            openFolderId={openFolderId}
            onOpen={onOpenFolder}
            actions={archiveOpen ? null : folderActions}
            rename={renamingFolder && renamingFolder.folderId === openFolderId ? renamingFolder.edit : null}
            archive={archiveOpen}
          />
        )}
        {headerEnd && <Box sx={{ flexShrink: 0, ml: 1 }}>{headerEnd}</Box>}
      </Box>

      {viewFilter?.active && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.5,
            backgroundColor: "rgba(0,212,173,0.08)",
            borderBottom: 1,
            borderColor: "divider",
            fontSize: "0.8rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {viewFilter.hiddenStories + viewFilter.hiddenFolders > 0
              ? `${[
                  viewFilter.hiddenStories
                    ? `${viewFilter.hiddenStories} story map${viewFilter.hiddenStories === 1 ? "" : "s"}`
                    : null,
                  viewFilter.hiddenFolders
                    ? `${viewFilter.hiddenFolders} folder${viewFilter.hiddenFolders === 1 ? "" : "s"}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" and ")} out of view on the map`
              : "Listing only what's on the map"}
          </Typography>
          <Button size="small" onClick={viewFilter.onShowAll} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
            Show all
          </Button>
        </Box>
      )}

      <TableContainer ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader size="small" sx={{ tableLayout: "auto", minWidth: 820 }}>
          <TableHead>
            <TableRow>
              {columns.map((c, i) => (
                <TableCell
                  key={`${c.label}-${i}`}
                  align={c.align}
                  sx={{
                    whiteSpace: "nowrap",
                    fontWeight: 700,
                    ...(c.frozenRight !== undefined ? frozenHeadSx(c.frozenRight, c.edgePad) : {}),
                  }}
                >
                  {c.key ? (
                    <TableSortLabel
                      active={sort?.key === c.key}
                      direction={sort?.key === c.key ? sort.dir : "asc"}
                      onClick={() => onSortChange(nextStorySort(sort, c.key as StorySortKey))}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : (
                    c.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "text.secondary" }}>
                  Loading story maps…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && error && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "error.main" }}>
                  Couldn't load story maps: {error.message}
                </TableCell>
              </TableRow>
            )}
            {showUpRow && (
              <UpRow
                parentName={archiveOpen ? "All projects" : parentName}
                colSpan={colCount}
                onClick={() => onOpenFolder(archiveOpen ? null : parentOfOpen)}
              />
            )}
            {showArchiveRow && archive && (
              <ArchiveRow
                count={archive.count}
                storyCount={archive.storyCount}
                colSpan={colCount}
                highlighted={false}
                isDropTarget={false}
                onOpen={archive.onOpen}
                onHoverChange={() => undefined}
                canLocate={archive.canLocate}
                onLocate={archive.onLocate}
                dropProps={NO_DRAG}
              />
            )}
            {!isLoading &&
              mode === "explorer" &&
              folders.map((folder) => (
                <FolderRow
                  key={`folder-${folder.id}`}
                  folder={folder}
                  counts={subtreeCounts(index, allProjects, folder.id, storyCount)}
                  colSpan={colCount}
                  viewOnly={viewOnly}
                  isDropTarget={false}
                  highlighted={highlightedFolderId === folder.id}
                  isPinned={!!folder.is_pinned}
                  onTogglePin={() => onToggleFolderPin(folder)}
                  canLocate={folderHasLocation(folder)}
                  onLocate={() => onLocateFolder(folder)}
                  mapVisible={folder.map_visible !== false}
                  hiddenBy={hiddenByFolder(index, folder.parent)?.name ?? null}
                  onToggleMapVisible={() => onToggleFolderMapVisible(folder)}
                  onOpen={() => onOpenFolder(folder.id)}
                  onHoverChange={hoverFolder(folder.id)}
                  onRename={() => onRequestRenameFolder(folder)}
                  rename={renamingFolder?.folderId === folder.id ? renamingFolder.edit : null}
                  onMove={() => onRequestMoveFolder(folder)}
                  onDelete={() => onRequestDeleteFolder(folder)}
                  onBoundary={() => onRequestFolderBoundary(folder)}
                  hasBoundary={!!folder.boundary_bbox}
                  dragProps={NO_DRAG}
                  dropProps={NO_DRAG}
                />
              ))}
            {!isLoading &&
              stories.map((story) => {
                const folderKey = folderKeyOf(story);
                return (
                  <StoryRow
                    key={story.id}
                    story={story}
                    scopeLabel={storyScopeLabel(story, index)}
                    showFolder={mode === "search" || archiveOpen}
                    folderLabel={pathLabel(index, folderKey)}
                    highlighted={highlightedStoryIds.has(story.id)}
                    viewOnly={viewOnly}
                    rowRef={(el) => {
                      rowRefs.current[story.id] = el;
                    }}
                    onHoverChange={hoverStory(story.id)}
                    onOpen={() => onOpenStory(story)}
                    onPreview={() => onPreviewStory(story)}
                    onCopyLink={() => onCopyShareLink(story)}
                    onDelete={() => onDeleteStory(story)}
                    canLocate={storyHasLocation(story)}
                    onLocate={() => onLocateStory(story)}
                  />
                );
              })}
            {!isLoading && !error && (explorerEmpty || searchEmpty) && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
                  {searchEmpty
                    ? "No story maps match your search."
                    : archiveOpen
                      ? "Story maps of archived projects aren't available. Restore the project to work on them."
                      : openFolderId === null
                        ? "No story maps yet. Start one from New → Story map, or from a project's Story maps page."
                        : "No story maps in this folder. Start one from New → Story map."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
